// Portado de src/components/Header.astro (site Astro).
import { useRouterState } from "@tanstack/react-router";
import {
  Activity,
  Dumbbell,
  Flame,
  HeartPulse,
  Laptop,
  MapPin,
  Newspaper,
  PlayCircle,
  Sparkles,
  VenusAndMars,
  type LucideIcon,
} from "lucide-react";

import { MENU, SITE, SUBTITULOS_TRATAMENTOS } from "@/lib/itv/site";
import { useAgendamentoLink } from "@/lib/itv/agendamento";

const PAINEIS_MENU = {
  Tratamentos: {
    rotulo: "TRATAMENTOS",
    titulo: "Um cuidado para cada objetivo",
    texto: "Toda consulta começa com avaliação completa e bioimpedância inclusa.",
    href: "/tratamentos/",
    link: "Ver todos",
  },
  Conteúdos: {
    rotulo: "CONTEÚDOS",
    titulo: "Informação médica para cuidar da saúde",
    texto: "Artigos e vídeos do Dr. Thiago Viana.",
    href: "/blog/",
    link: "Ver conteúdos",
  },
  Contato: {
    rotulo: "ATENDIMENTO",
    titulo: "Escolha como deseja ser atendido",
    texto: "Consulta presencial em Bauru ou teleconsulta.",
    href: "/bauru/",
    link: "Ver consultório",
  },
} as const;

const ICONES_MENU: Record<string, LucideIcon> = {
  "Emagrecimento e saúde metabólica": Flame,
  "Saúde hormonal": VenusAndMars,
  "Massa muscular e sarcopenia": Dumbbell,
  "Medicina do esporte": Activity,
  "Longevidade e qualidade de vida": HeartPulse,
  "Estética e medicina regenerativa": Sparkles,
  Blog: Newspaper,
  Vídeos: PlayCircle,
  "Consultório em Bauru": MapPin,
  Teleconsulta: Laptop,
};

const SUBTITULOS_MENU: Record<string, string> = {
  Blog: "Artigos sobre saúde, nutrologia e qualidade de vida",
  Vídeos: "Conteúdo em vídeo com o Dr. Thiago Viana",
  "Consultório em Bauru": "Atendimento presencial no ITV",
  Teleconsulta: "Acompanhamento médico a distância",
};

export function Header() {
  const agendar = useAgendamentoLink();
  const atual = useRouterState({ select: (estado) => estado.location.pathname });
  const ativo = (href?: string) => (href && atual === href ? ("page" as const) : undefined);

  const itemTratamentos = MENU.find((i) => i.label === "Tratamentos");
  const tratamentos = itemTratamentos?.filhos ?? [];
  const hrefTratamentos = itemTratamentos?.href ?? "/tratamentos/";

  return (
    <header className="topo">
      <div className="barra">
        <a className="marca" href="/">
          <img
            src="/img/logo-itv-83.webp"
            srcSet="/img/logo-itv-83.webp 1x, /img/logo-itv-166.webp 2x, /img/logo-itv-249.webp 3x"
            width={83}
            height={44}
            loading="eager"
            fetchPriority="low"
            alt={`${SITE.clinica} — ir para a página inicial`}
          />
        </a>

        <nav className="nav" aria-label="Principal">
          <ul className="nivel1">
            {MENU.map((item) => {
              const temPainel = item.label in PAINEIS_MENU;
              return (
                <li
                  key={item.label}
                  className={temPainel ? "tem-mega" : item.filhos ? "tem-filhos" : ""}
                  data-menu={temPainel ? item.label : undefined}
                >
                  {item.href ? (
                    <a href={item.href} aria-current={ativo(item.href)}>
                      {item.label}
                    </a>
                  ) : (
                    <span className="nav-rotulo">{item.label}</span>
                  )}
                  {item.filhos && !temPainel && (
                    <ul className="nivel2">
                      {item.filhos.map((sub) => (
                        <li key={sub.label}>
                          <a href={sub.href} aria-current={ativo(sub.href)}>
                            {sub.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <a className="btn-ouro agendar" href={agendar}>
          Agendar consulta
        </a>

        {/* Menu do celular: <details> abre e fecha sem JavaScript. */}
        <details className="menu-mobile">
          <summary aria-label="Abrir o menu" aria-controls="menu-celular">
            <span className="hamburguer" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </summary>
        </details>
        <div className="painel-mobile" id="menu-celular">
          <nav aria-label="Principal (celular)">
            <p className="rotulo-menu">Navegação</p>
            <ul>
              {MENU.map((item) => (
                <li key={item.label}>
                  {item.href ? (
                    <a href={item.href} aria-current={ativo(item.href)}>
                      {item.label}
                    </a>
                  ) : (
                    <span className="grupo">{item.label}</span>
                  )}
                  {item.filhos && (
                    <ul className="sub">
                      {item.filhos.map((sub) => (
                        <li key={sub.label}>
                          <a href={sub.href} aria-current={ativo(sub.href)}>
                            {sub.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          <a className="btn-ouro agendar-mobile" href={agendar}>
            Agendar consulta
          </a>
        </div>
      </div>

      {MENU.filter((item) => item.label in PAINEIS_MENU).map((item) => {
        const painel = PAINEIS_MENU[item.label as keyof typeof PAINEIS_MENU];
        const itens = item.label === "Tratamentos" ? tratamentos : item.filhos ?? [];
        return (
          <div className="mega" data-painel={item.label} key={item.label}>
            <div className="mega-inner">
              <div className="mega-intro">
                <p className="mega-rotulo">{painel.rotulo}</p>
                <p className="mega-titulo">{painel.titulo}</p>
                <p className="mega-texto">{painel.texto}</p>
                <a className="mega-todos" href={painel.href}>{painel.link} →</a>
              </div>

              <ul className={`mega-grade ${itens.length === 2 ? "mega-grade-curta" : ""}`}>
                {itens.map((itemMenu) => {
                  const Icone = ICONES_MENU[itemMenu.label];
                  const subtitulo = item.label === "Tratamentos"
                    ? SUBTITULOS_TRATAMENTOS[itemMenu.label]
                    : SUBTITULOS_MENU[itemMenu.label];
                  return (
                    <li key={itemMenu.label}>
                      <a className="mega-item" href={itemMenu.href}>
                        <span className="mega-thumb" aria-hidden="true">
                          {Icone ? <Icone size={20} strokeWidth={1.5} /> : null}
                        </span>
                        <span className="mega-item-texto">
                          <span className="mega-item-titulo">{itemMenu.label}</span>
                          <span className="mega-item-sub">{subtitulo}</span>
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        );
      })}
    </header>
  );
}
